import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import images from '../../data/p15images.json';
import { P15Image } from './P15Image';

export const P15Lightbox = ({ item, onClose }) => {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [item]);
  if (!item) return null;
  const alt = images[item.group]?.[item.id]?.alt;
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/85 overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true" aria-label={item.title} data-testid="p15-lightbox">
      <div className="min-h-full flex items-start justify-center sm:p-6">
        <div className="w-full max-w-3xl bg-gray-50 sm:rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="p15-lightbox-panel">
          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 md:px-7 pt-5 pb-3 bg-white border-b border-gray-100">
            <div className="min-w-0">
              <span className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full mb-1.5 bg-teal-50 text-teal-700 break-words" data-testid="p15-lightbox-chip">{item.chip}</span>
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-tight break-words" data-testid="p15-lightbox-title">{item.title}</h2>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2.5 shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors" style={{ minWidth: 44, minHeight: 44 }} data-testid="p15-lightbox-close"><X size={18} className="mx-auto" /></button>
          </div>
          <div className="px-4 md:px-6 pt-4">
            <P15Image group={item.group} id={item.id} sizes="(max-width: 768px) 100vw, 768px" priority contain className="rounded-2xl shadow-sm" testId="p15-lightbox-image" />
            {alt && <p className="text-xs text-gray-600 mt-2 leading-relaxed" data-testid="p15-lightbox-caption">{alt}</p>}
          </div>
          <div className="px-4 md:px-6 py-5 space-y-4">
            {item.sections.map(([label, body, Icon, cardCls, iconCls], i) => (
              <section key={label} className={`bg-white border-2 ${cardCls} rounded-3xl p-5 md:p-7 shadow-sm min-w-0`} data-testid={`p15-lightbox-section-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${iconCls} text-white flex items-center justify-center shrink-0 shadow-sm`}><Icon size={18} /></span>
                  <h3 className="text-lg md:text-xl font-extrabold text-gray-900 break-words">{label}</h3>
                </div>
                <div className="text-gray-700 text-sm md:text-base leading-relaxed break-words">{body}</div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
